// File: client/src/components/rules/expressionEditor/GroupRow.js
import React from 'react';
import { Row, Space, Button, Card, Tag } from 'antd';
import { PlusOutlined, DeleteOutlined, SplitCellsOutlined as UngroupOutlined } from '@ant-design/icons';
import { LOGIC_OPERATORS } from './constants';
import ConditionRow from './ConditionRow';

const GroupRow = ({
                      group,
                      onUpdate,
                      onDelete,
                      onAddCondition,
                      onUngroup,
                      availableFields,
                      onHighlight,
                      isHighlighted,
                      nestingLevel = 0,
                      selectedNodeIds = []
                  }) => {
    const getBackgroundColor = () => {
        if (isHighlighted) return 'white'; // No background color for selection

        // Alternate background colors based on nesting level
        return nestingLevel % 2 === 0 ? '#f5f5f5' : '#e8e8e8';
    };

    // Toggle the operator when clicking the operator tag
    const toggleOperator = () => {
        const newOperator = group.operator === 'and' ? 'or' : 'and';
        onUpdate({ ...group, operator: newOperator });
    };

    return (
        <Card
            size="small"
            style={{
                marginBottom: '8px',
                border: isHighlighted ? '2px solid #1890ff' : '1px solid #d9d9d9',
                backgroundColor: getBackgroundColor()
            }}
            bodyStyle={{ padding: '12px' }}
            onClick={(e) => {
                e.stopPropagation();
                onHighlight && onHighlight(group.id, false); // Allow multiple selection
            }}
        >
            <Space direction="vertical" style={{ width: '100%' }}>
                {group.conditions.map((condition, index) => (
                    <React.Fragment key={condition.id}>
                        {index > 0 && (
                            <div style={{ textAlign: 'center', margin: '4px 0' }}>
                                <Tag
                                    color={group.operator === 'and' ? 'blue' : 'orange'}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggleOperator();
                                    }}
                                    style={{ cursor: 'pointer' }}
                                >
                                    {LOGIC_OPERATORS[group.operator].label}
                                </Tag>
                            </div>
                        )}
                        {condition.type === 'condition' ? (
                            <ConditionRow
                                condition={condition}
                                onUpdate={(updated) => {
                                    const newConditions = [...group.conditions];
                                    const index = newConditions.findIndex(c => c.id === updated.id);
                                    if (index !== -1) {
                                        newConditions[index] = updated;
                                        onUpdate({ ...group, conditions: newConditions });
                                    }
                                }}
                                onDelete={(id) => {
                                    const newConditions = group.conditions.filter(c => c.id !== id);
                                    if (newConditions.length === 0) {
                                        onDelete(group.id);
                                    } else {
                                        onUpdate({ ...group, conditions: newConditions });
                                    }
                                }}
                                availableFields={availableFields}
                                disableDelete={group.conditions.length <= 1}
                                onHighlight={(id) => {
                                    // Allow selecting individual conditions within groups
                                    if (onHighlight) {
                                        onHighlight(id, false);
                                    }
                                }}
                                isHighlighted={selectedNodeIds.includes(condition.id)}
                            />
                        ) : (
                            <GroupRow
                                group={condition}
                                onUpdate={(updated) => {
                                    const newConditions = [...group.conditions];
                                    const index = newConditions.findIndex(c => c.id === updated.id);
                                    if (index !== -1) {
                                        newConditions[index] = updated;
                                        onUpdate({ ...group, conditions: newConditions });
                                    }
                                }}
                                onDelete={(id) => {
                                    const newConditions = group.conditions.filter(c => c.id !== id);
                                    if (newConditions.length === 0) {
                                        onDelete(group.id);
                                    } else {
                                        onUpdate({ ...group, conditions: newConditions });
                                    }
                                }}
                                onAddCondition={onAddCondition}
                                onUngroup={onUngroup}
                                availableFields={availableFields}
                                selectedNodeIds={selectedNodeIds}
                                onHighlight={(id) => {
                                    // Allow selecting individual conditions in nested groups
                                    if (onHighlight) {
                                        onHighlight(id, false);
                                    }
                                }}
                                nestingLevel={nestingLevel + 1}
                                isHighlighted={selectedNodeIds.includes(condition.id)}
                            />
                        )}
                    </React.Fragment>
                ))}

                <Row justify="end">
                    <Space size={0}>
                        <Button
                            type="text"
                            icon={<PlusOutlined />}
                            onClick={(e) => {
                                e.stopPropagation();
                                onAddCondition(group.id);
                            }}
                            title="Add condition to group"
                            style={{ padding: '0 4px' }}
                        />
                        {onUngroup && (
                            <Button
                                type="text"
                                icon={<UngroupOutlined />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onUngroup(group.id);
                                }}
                                title="Ungroup conditions"
                                style={{ padding: '0 4px' }}
                            />
                        )}
                        <Button
                            type="text"
                            icon={<DeleteOutlined />}
                            onClick={(e) => {
                                e.stopPropagation();
                                onDelete(group.id);
                            }}
                            danger
                            title="Delete group"
                            style={{ padding: '0 4px' }}
                        />
                    </Space>
                </Row>
            </Space>
        </Card>
    );
};

export default GroupRow;
